import { FieldMappingBaseFieldType } from '@monetize/types/app';
import { MFlex } from '../../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { Highlighter } from '../../../../../../components/Monetize/MPageSearchInput/Highlighter';
import { CrmFieldMappingSearchInput } from './CrmFieldMappingSearchInput';

type CrmFieldMappingDropdownProps = {
  placeholder: string;
  fields: FieldMappingBaseFieldType[];
  fieldProps: any;
  index: number;
  handleOnClear: (idx: number, side: 'source' | 'target') => void;
  onChange: (value: string) => void;
  validateData: (index: number) => void;
  getVariant: (idx: number, side: 'source' | 'target') => 'unsaved' | undefined;
  changeMode: (ev: any, idx: number) => void;
  setAlert: (ev: any, idx: number) => void;
  /** Which side of the mapping this dropdown controls in the form. */
  side: 'source' | 'target';
  /** Whether this dropdown lists MonetizeNow fields (drives ref-lookup mode). */
  isMNField?: boolean;
  isDisabled?: boolean;
};

export const CrmFieldMappingDropdown = ({
  placeholder,
  fields,
  fieldProps,
  index,
  handleOnClear,
  onChange,
  validateData,
  getVariant,
  changeMode,
  setAlert,
  side,
  isMNField,
  isDisabled,
}: CrmFieldMappingDropdownProps) => {
  return (
    <MCustomSelect
      isLazy
      isDisabled={isDisabled}
      returnItem
      clearable
      onClear={() => handleOnClear(index, side)}
      onChange={(ev) => {
        onChange(ev as any);
        validateData(index);
        isMNField && changeMode(ev, index);
        setAlert(ev, index);
      }}
      itemTitle="displayLabel"
      itemValue="fieldApiName"
      useMainInputAsSearchInput
      internalFilterFields={['displayLabel', 'fieldApiName', 'dataType']}
      MainInputComponent={CrmFieldMappingSearchInput}
      placeholder={placeholder}
      items={fields}
      useHighlightedItemList
      showItemDivider
      renderItemContent={({ item, query }) => {
        const { dataType, displayLabel, fieldApiName, isCustom } =
          item as FieldMappingBaseFieldType;
        // Custom fields aren't obvious from their name, so surface them with a
        // `customField.` prefix (display only; the stored fieldApiName is bare).
        const fieldApiNameDisplay = isCustom
          ? `customField.${fieldApiName}`
          : fieldApiName;
        return (
          <MFlex
            direction="column"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            <Highlighter
              title={displayLabel}
              color="tPurple.dark"
              fontSize="sm"
              fontWeight="medium"
              textToHighlight={displayLabel}
              searchWords={[query]}
            />
            <Highlighter
              title={fieldApiNameDisplay}
              color="tGray.darkPurple"
              fontSize="xs"
              textToHighlight={fieldApiNameDisplay}
              searchWords={[query]}
            />
            <Highlighter
              title={dataType}
              color="tGray.darkPurple"
              fontSize="xs"
              textTransform="capitalize"
              textToHighlight={dataType}
              searchWords={[query]}
            />
          </MFlex>
        );
      }}
      inputProps={{
        autoComplete: 'off',
        variant: getVariant(index, side),
      }}
      popOverContentProps={{
        width: 'lg',
      }}
      {...fieldProps}
    />
  );
};
